import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
  useProvisionMember,
  useSendActivationLink,
  isSeatCapError,
} from '@/hooks/useProvisionEnterpriseMember';

/**
 * Enterprise "Add member" dialog (#609, Phase 1).
 *
 * Email-only (the member's role is server-derived from the partner's
 * provisioning_mode — no role select). On submit it calls the
 * enterprise-provision-member edge fn via useProvisionMember, which creates the
 * member's account + workspace instantly (an instant seat). A `licensed`
 * partner at its cap is rejected: we keep the dialog open and show the
 * contact-us copy inline. Other errors → destructive toast + GlitchTip.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SEAT_CAP_MESSAGE =
  "You've reached your licensed seat limit — contact BuildIt to add seats.";

interface AddMemberDialogProps {
  partnerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddMemberDialog({
  partnerId,
  open,
  onOpenChange,
}: AddMemberDialogProps): JSX.Element {
  const { toast } = useToast();
  const provision = useProvisionMember();
  const sendActivationLink = useSendActivationLink();
  const [email, setEmail] = useState('');
  const [capError, setCapError] = useState<string | null>(null);
  // #615: default to emailing the new member a first-login link so they can
  // sign in (and become emulatable) without an admin chasing them.
  const [sendLink, setSendLink] = useState(true);

  const handleClose = (next: boolean) => {
    if (!next) {
      setEmail('');
      setCapError(null);
      setSendLink(true);
    }
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    const trimmed = email.trim().toLowerCase();
    setCapError(null);
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      toast({
        variant: 'destructive',
        title: 'Invalid email',
        description: 'Enter a valid email address.',
      });
      return;
    }
    try {
      const result = await provision.mutateAsync({ partner_id: partnerId, email: trimmed });

      // Best-effort first-login link (#615). The add already succeeded — if the
      // link can't be sent we still close + report success, but warn separately.
      let linkSent = false;
      if (sendLink && result.buildit_user_id) {
        try {
          await sendActivationLink.mutateAsync({
            partner_id: partnerId,
            user_id: result.buildit_user_id,
          });
          linkSent = true;
        } catch (linkErr) {
          // captureError is handled inside the hook's onError (gated on
          // not_eligible). Surface a non-blocking warning to the admin.
          toast({
            variant: 'destructive',
            title: 'Member added, but link not sent',
            description:
              linkErr instanceof Error
                ? linkErr.message
                : "We couldn't send the first-login link. You can resend it from the Members page.",
          });
        }
      }

      toast({
        title: 'Member added',
        description: linkSent
          ? `${trimmed} now has a seat — we emailed them a first-login link.`
          : `${trimmed} now has a seat in your enterprise.`,
      });
      handleClose(false);
    } catch (err) {
      if (isSeatCapError(err)) {
        // Keep the dialog open; show the contact-us copy inline + a toast.
        setCapError(SEAT_CAP_MESSAGE);
        toast({ variant: 'destructive', title: 'Seat limit reached', description: SEAT_CAP_MESSAGE });
        return;
      }
      // Genuine (non-cap) provision failures are already captured to GlitchTip by
      // useProvisionMember.onError — don't double-report here.
      toast({
        variant: 'destructive',
        title: 'Could not add member',
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add member</DialogTitle>
          <DialogDescription>
            Add someone to your enterprise. They get an account and workspace
            instantly &mdash; no invitation email required.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="add-member-email">
              Email
            </label>
            <Input
              id="add-member-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="add-member-send-link"
              checked={sendLink}
              onCheckedChange={(checked) => setSendLink(checked === true)}
              className="mt-0.5"
            />
            <label
              htmlFor="add-member-send-link"
              className="text-sm text-muted-foreground leading-snug"
            >
              Email them a first-login link so they can sign in
            </label>
          </div>
          {capError && (
            <p className="text-sm text-destructive" role="alert">
              {capError}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            onClick={handleSubmit}
            disabled={provision.isPending || sendActivationLink.isPending}
          >
            {provision.isPending || sendActivationLink.isPending ? 'Adding…' : 'Add member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
